import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { compact, omit } from 'lodash-es';
import { notEmpty } from '~/infra/utils';
import * as approvalMethodNoticePeriodModel from '~/models/approval-method-notice-period';
import { toCustomPageDTO } from '~/models/custom-page/model';
import { hasSignatureTerms } from '~/types/api/common/AgreementLegalTerms/AgreementLegalTerms';
import { savedAgreementTax } from '~/utils/salesTax';
import { fromDatestampObjectToExpirationDate } from './expiration-date';
import * as serviceModel from './service';
import { mapBundledServices } from './service/mapServices';
import { formatDateLong, fromDateStampObject } from './time';
export const counterSignatureTypeFromInclude = (include)=>include == null ? 'none' : include ? 'including' : 'notIncluding';
export const includeFromCounterSignatureType = (counterSignatureType)=>{
    if (counterSignatureType === 'including') {
        return true;
    }
    if (counterSignatureType === 'notIncluding') {
        return false;
    }
    return null;
};
export var DraftStatusType = /*#__PURE__*/ function(DraftStatusType) {
    DraftStatusType["Active"] = "active";
    DraftStatusType["Scheduled"] = "scheduled";
    DraftStatusType["Archived"] = "archived";
    DraftStatusType["None"] = "none";
    return DraftStatusType;
}({});
export class DraftModel {
    static fromDTO(dto) {
        var _dto_customerId, _dto_legalTermsDocuments, _dto_reviewers, _dto_referenceFields;
        return new DraftModel(_object_spread_props(_object_spread({}, dto), {
            customerId: (_dto_customerId = dto.customerId) !== null && _dto_customerId !== void 0 ? _dto_customerId : null,
            legalTermsDocuments: (_dto_legalTermsDocuments = dto.legalTermsDocuments) !== null && _dto_legalTermsDocuments !== void 0 ? _dto_legalTermsDocuments : [],
            reviewers: (_dto_reviewers = dto.reviewers) !== null && _dto_reviewers !== void 0 ? _dto_reviewers : [],
            metadata: dto.metadata,
            referenceFields: (_dto_referenceFields = dto.referenceFields) !== null && _dto_referenceFields !== void 0 ? _dto_referenceFields : undefined,
            services: compact(dto.services.map(serviceModel.fromDTO)),
            serviceBundles: dto.serviceBundles ? {
                bundles: dto.serviceBundles.bundles.map((bundle)=>_object_spread_props(_object_spread({}, bundle), {
                        services: compact(bundle.services.map(serviceModel.fromDTO))
                    }))
            } : undefined
        }));
    }
    static fromRelationship(relationship) {
        return new DraftModel({
            billingScheduleDisplay: relationship.billingScheduleDisplay,
            id: relationship.id,
            coverLetter: relationship.customNotificationText,
            extraClause: relationship.extraClause || '',
            requirePaymentMethod: relationship.requirePaymentMethod != null ? relationship.requirePaymentMethod : true,
            allowManualPayments: relationship.allowManualPayments,
            isExistingCustomer: false,
            customNotificationText: relationship.customNotificationText,
            title: relationship.title,
            lastModifiedOn: 0,
            lastModifiedBy: null,
            services: relationship.services,
            serviceBundles: relationship.serviceBundles,
            netTerms: relationship.netTerms,
            effectiveDate: relationship.effectiveDate,
            feeControls: relationship.feeControls,
            clientContactId: relationship.clientContactId,
            customerId: relationship.customer.id,
            isSample: relationship.isSample,
            amendmentsApprovalMethod: relationship.amendmentsApprovalMethod,
            invoicesApprovalMethod: relationship.invoicesApprovalMethod,
            expirationDate: fromDatestampObjectToExpirationDate(relationship.expirationDate, relationship.createdOn),
            legalTermsDocuments: relationship.legalTermsDocuments,
            reviewers: [
                ...(relationship.signatories || []).map((s)=>({
                        firstName: s.firstName || undefined,
                        lastName: s.lastName || undefined,
                        email: s.email,
                        isSignatory: true
                    })),
                ...relationship.reviewers.map((r)=>({
                        firstName: r.firstName || undefined,
                        lastName: r.lastName || undefined,
                        email: r.email,
                        isSignatory: false
                    }))
            ],
            metadata: undefined,
            customPages: relationship.customPages,
            referenceFields: relationship.referenceFields,
            status: {
                type: "active"
            }
        });
    }
    toDTO() {
        var _this_data_customPages;
        const dto = _object_spread_props(_object_spread({}, omit(this.data, 'signersLocked')), {
            services: this.data.services.map(serviceModel.toDTO),
            legalTermsDocuments: this.data.legalTermsDocuments.filter(notEmpty),
            serviceBundles: this.data.serviceBundles != null ? {
                bundles: this.data.serviceBundles.bundles.map((bundle)=>_object_spread_props(_object_spread({}, bundle), {
                        services: bundle.services.map(serviceModel.toDTO)
                    }))
            } : undefined,
            customPages: (_this_data_customPages = this.data.customPages) === null || _this_data_customPages === void 0 ? void 0 : _this_data_customPages.map(toCustomPageDTO)
        });
        if (dto.customerId == null) {
            delete dto.customerId;
        }
        return dto;
    }
    id() {
        return this.data.id;
    }
    billingScheduleDisplay() {
        return this.data.billingScheduleDisplay;
    }
    coverLetter() {
        return this.data.coverLetter;
    }
    extraClause() {
        return this.data.extraClause;
    }
    requirePaymentMethod() {
        return this.data.requirePaymentMethod;
    }
    includeCounterSignature() {
        return includeFromCounterSignatureType(this.data.counterSignatureType);
    }
    allowWithoutPaymentMethod() {
        return !this.data.requirePaymentMethod;
    }
    allowManualPayments() {
        return this.data.allowManualPayments;
    }
    isExistingCustomer() {
        return this.data.isExistingCustomer;
    }
    customNotificationText() {
        return this.data.customNotificationText;
    }
    title() {
        return this.data.title;
    }
    lastModifiedOn() {
        return this.data.lastModifiedOn;
    }
    lastModifiedBy() {
        return this.data.lastModifiedBy;
    }
    services() {
        return this.data.services;
    }
    serviceBundles() {
        return this.data.serviceBundles ? this.data.serviceBundles.bundles : [];
    }
    serviceInstances() {
        return {
            services: this.services(),
            serviceBundles: {
                bundles: this.serviceBundles()
            }
        };
    }
    netTerms() {
        return this.data.netTerms;
    }
    effectiveDate() {
        return this.data.effectiveDate;
    }
    effectiveDateAsDate() {
        return this.data.effectiveDate ? fromDateStampObject(this.data.effectiveDate) : null;
    }
    formattedEffectiveDate() {
        return this.data.effectiveDate ? formatDateLong(this.data.effectiveDate) : null;
    }
    legalTermsDocuments() {
        return this.data.legalTermsDocuments;
    }
    coverCreditCardFees() {
        return this.data.feeControls.coverCreditCardFees;
    }
    clientContactId() {
        return this.data.clientContactId;
    }
    customerId() {
        return this.data.customerId;
    }
    isSample() {
        return this.data.isSample;
    }
    amendmentsApprovalMethod() {
        return this.data.amendmentsApprovalMethod;
    }
    invoicesApprovalMethod() {
        return this.data.invoicesApprovalMethod;
    }
    reviewers() {
        return this.data.reviewers;
    }
    expirationDate() {
        return this.data.expirationDate;
    }
    linkedRelationshipId() {
        var _this_data_linkedRelationshipId;
        return (_this_data_linkedRelationshipId = this.data.linkedRelationshipId) !== null && _this_data_linkedRelationshipId !== void 0 ? _this_data_linkedRelationshipId : null;
    }
    signersLocked() {
        var _this_data_signersLocked;
        return (_this_data_signersLocked = this.data.signersLocked) !== null && _this_data_signersLocked !== void 0 ? _this_data_signersLocked : false;
    }
    hasSignatureTerms() {
        return hasSignatureTerms(this.data.legalTermsDocuments);
    }
    metadata() {
        return this.data.metadata;
    }
    templateVariableValues() {
        return this.data.templateVariableValues;
    }
    customPages() {
        var _this_data_customPages;
        return (_this_data_customPages = this.data.customPages) !== null && _this_data_customPages !== void 0 ? _this_data_customPages : [];
    }
    referenceFields() {
        var _this_data_referenceFields;
        return (_this_data_referenceFields = this.data.referenceFields) !== null && _this_data_referenceFields !== void 0 ? _this_data_referenceFields : [];
    }
    requireCustomerAddress() {
        var _this_data_requireCustomerAddress;
        return (_this_data_requireCustomerAddress = this.data.requireCustomerAddress) !== null && _this_data_requireCustomerAddress !== void 0 ? _this_data_requireCustomerAddress : false;
    }
    emptyReferenceField() {
        var _this_referenceFields_find;
        return (_this_referenceFields_find = this.referenceFields().find((field)=>field.value.trim() === '')) !== null && _this_referenceFields_find !== void 0 ? _this_referenceFields_find : null;
    }
    tax() {
        return savedAgreementTax(this.data.tax);
    }
    resolvedTax() {
        return this.data.resolvedTax;
    }
    setCustomPages(pages) {
        return this.setChanges({
            customPages: pages
        });
    }
    service(serviceId) {
        if (this.data.serviceBundles) {
            return this.data.serviceBundles.bundles.flatMap((b)=>b.services).find((s)=>s.id === serviceId);
        }
        return this.data.services.find((s)=>s.id === serviceId);
    }
    hasNoServicesOrBundles() {
        return !this.data.serviceBundles && !this.data.services.length;
    }
    expirationDaysAfterSending() {
        var _this_data_expirationDate;
        return ((_this_data_expirationDate = this.data.expirationDate) === null || _this_data_expirationDate === void 0 ? void 0 : _this_data_expirationDate.type) === 'days' ? this.data.expirationDate.days : null;
    }
    defaultExpirationDays(worksAt) {
        var _worksAt_settings_expirationDate, _worksAt_settings;
        return (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : (_worksAt_settings_expirationDate = _worksAt_settings.expirationDate) === null || _worksAt_settings_expirationDate === void 0 ? void 0 : _worksAt_settings_expirationDate.type) === 'days' ? worksAt.settings.expirationDate.days : 30;
    }
    defaultAutoApprovalMethod(worksAt) {
        var _worksAt_settings;
        return (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.amendmentsApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod) && worksAt.settings.amendmentsApprovalMethod.type === 'auto' ? worksAt.settings.amendmentsApprovalMethod.auto.noticePeriodDays : 30;
    }
    defaultAutoApproveOutOfScopeCharges(worksAt) {
        var _worksAt_settings;
        return (worksAt === null || worksAt === void 0 ? void 0 : (_worksAt_settings = worksAt.settings) === null || _worksAt_settings === void 0 ? void 0 : _worksAt_settings.invoicesApprovalMethod) && approvalMethodNoticePeriodModel.isAutoApproval(worksAt.settings.invoicesApprovalMethod) && worksAt.settings.invoicesApprovalMethod.type === 'auto' ? worksAt.settings.invoicesApprovalMethod.auto.noticePeriodDays : 30;
    }
    isScheduled() {
        return this.data.status.type === "scheduled";
    }
    scheduledDate() {
        return this.data.status.type === "scheduled" ? this.data.status.schedule.time : null;
    }
    addServices(services, packageId, anchor) {
        const anchored = services.map((service)=>serviceModel.withStageAnchor(service, anchor));
        if (packageId) {
            if (this.data.serviceBundles) {
                const newBundles = this.data.serviceBundles.bundles.map((b)=>{
                    if (b.id === packageId) {
                        return _object_spread_props(_object_spread({}, b), {
                            services: b.services.concat(anchored)
                        });
                    }
                    return b;
                });
                return this.setServiceBundles(newBundles);
            } else {
                return this;
            }
        } else {
            return this.setServices(this.data.services.concat(anchored));
        }
    }
    setServices(services) {
        return this.setChanges({
            services
        });
    }
    setServiceBundles(bundles) {
        return this.setChanges({
            serviceBundles: bundles.length > 0 ? {
                bundles
            } : undefined
        });
    }
    setService(serviceId, transformer) {
        if (this.data.serviceBundles) {
            const newBundles = this.data.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                    services: b.services.map((s)=>s.id === serviceId ? transformer(s) : s)
                }));
            return this.setChanges({
                serviceBundles: {
                    bundles: newBundles
                }
            });
        } else {
            return this.setChanges({
                services: this.data.services.map((s)=>s.id === serviceId ? transformer(s) : s)
            });
        }
    }
    removeService(serviceId) {
        if (this.data.serviceBundles) {
            return this.setChanges({
                serviceBundles: {
                    bundles: this.data.serviceBundles.bundles.map((bundle)=>_object_spread_props(_object_spread({}, bundle), {
                            services: bundle.services.filter((s)=>s.id !== serviceId)
                        }))
                }
            });
        }
        return this.setChanges({
            services: this.data.services.filter((s)=>s.id !== serviceId)
        });
    }
    setPackageName(packageId, packageName) {
        if (!this.data.serviceBundles) {
            return this;
        }
        return this.setChanges({
            serviceBundles: {
                bundles: this.data.serviceBundles.bundles.map((bundle)=>bundle.id === packageId ? _object_spread_props(_object_spread({}, bundle), {
                        name: packageName
                    }) : bundle)
            }
        });
    }
    setLegalTermsDocuments(legalTermsDocuments) {
        return this.setChanges({
            legalTermsDocuments
        });
    }
    setRequirePaymentMethod(requirePaymentMethod) {
        return this.setChanges({
            requirePaymentMethod
        });
    }
    setNetTerms(netTerms) {
        return this.setChanges({
            netTerms
        });
    }
    setAllowManualPayments(allowManualPayments) {
        return this.setChanges({
            allowManualPayments
        });
    }
    setCustomNotificationText(customNotificationText) {
        return this.setChanges({
            customNotificationText
        });
    }
    setExpirationDate(expirationDate) {
        return this.setChanges({
            expirationDate
        });
    }
    setEffectiveDate(effectiveDate, anchor) {
        const reanchor = (service)=>serviceModel.withStageAnchor(service, anchor);
        return this.setChanges(_object_spread({
            effectiveDate,
            services: this.data.services.map(reanchor)
        }, this.data.serviceBundles && {
            serviceBundles: {
                bundles: mapBundledServices(this.data.serviceBundles.bundles, reanchor)
            }
        }));
    }
    setCoverCreditCardFees(coverCreditCardFees) {
        return this.setChanges({
            feeControls: _object_spread_props(_object_spread({}, this.data.feeControls), {
                coverCreditCardFees
            })
        });
    }
    setSampleContact() {
        return this.setChanges({
            clientContactId: null,
            customerId: null,
            isSample: true
        });
    }
    setClientContactId(clientContactId) {
        return this.setChanges({
            clientContactId,
            isSample: false
        });
    }
    setClient(customerId) {
        return this.setChanges({
            customerId,
            clientContactId: null,
            reviewers: [],
            isSample: false
        });
    }
    setInvoicesApprovalMethod(invoicesApprovalMethod) {
        return this.setChanges({
            invoicesApprovalMethod
        });
    }
    setAmendmentsApprovalMethod(amendmentsApprovalMethod) {
        return this.setChanges({
            amendmentsApprovalMethod
        });
    }
    setExtraClause(extraClause) {
        return this.setChanges({
            extraClause
        });
    }
    setTitle(title) {
        return this.setChanges({
            title
        });
    }
    setTemplateVariableValues(templateVariableValues) {
        return this.setChanges({
            templateVariableValues
        });
    }
    setReviewers(reviewers) {
        return this.setChanges({
            reviewers
        });
    }
    setLastModifiedOn(lastModifiedOn) {
        return this.setChanges({
            lastModifiedOn
        });
    }
    setId(id) {
        return this.setChanges({
            id
        });
    }
    setChanges(changes) {
        return new DraftModel(_object_spread_props(_object_spread({}, this.data, changes), {
            id: changes.id || this.data.id
        }));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
